// ─────────────────────────────────────────────────────────────────────────────
// AudioService.js  —  Deepgram Nova-2 real-time streaming STT
// Replaces browser SpeechRecognition with sub-300ms Deepgram transcription.
// Same public API: onTranscript({ final, interim }), onStatus(), start(), stop(), restart()
// ─────────────────────────────────────────────────────────────────────────────

import { getToken } from './AuthService';

// The browser gets a short-lived Deepgram token from our backend right before it
// connects (gate d47c14f2). No permanent key ships in the bundle; the WS still
// goes straight to Deepgram — the proven path.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const MAX_TOKEN_RETRIES = 5;
const MAX_CONNECT_FAILURES = 5;

class TokenAuthError extends Error {}

// Returns { token, scheme }. Throws TokenAuthError on 401 (caller must not retry).
async function fetchDeepgramToken() {
  const res = await fetch(`${API_URL}/api/deepgram/token`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (res.status === 401) throw new TokenAuthError('not signed in');
  if (!res.ok) throw new Error(`token request failed (${res.status})`);
  const { token, scheme } = await res.json();
  if (!token) throw new Error('token request returned no token');
  return { token, scheme: scheme || 'bearer' };
}

// Minimal proven params only — no_delay/filler_words are NOT valid listen-endpoint params
const DG_URL =
  'wss://api.deepgram.com/v1/listen' +
  '?model=nova-2' +
  '&language=en-US' +
  '&smart_format=true' +
  '&punctuate=true' +
  '&interim_results=true' +
  '&endpointing=1500' +
  '&utterance_end_ms=2000';

class AudioService {
  constructor() {
    this.socket    = null;
    this.recorder  = null;
    this.stream    = null;

    this.isListening  = false;
    this.userStopped  = false;

    this.reconnTimer   = null;
    this.keepAliveInt  = null;
    this.reconnDelay   = 1000;
    this._connectSeq   = 0;   // bumps each _connect(); stale async attempts bail
    this._tokenRetries = 0;   // consecutive token-fetch failures
    this._connFailures = 0;   // consecutive sockets that closed before opening
    this._gaveUp       = false; // set by _giveUp(); blocks any reconnect until start()

    this._buf = '';   // accumulates is_final words within one utterance
    this._discardUntil = 0; // drop late finals flushed right after a Send

    this.onTranscriptCallback = null;
    this.onStatusCallback     = null;
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  async start() {
    if (this.isListening) return;
    this.userStopped   = false;
    this._gaveUp       = false;
    this.reconnDelay   = 1000;
    this._tokenRetries = 0;
    this._connFailures = 0;
    this._buf          = '';

    if (!this.stream) {
      const ok = await this._getMic();
      if (!ok) return;
    }

    this.isListening = true;
    this._connect();
  }

  stop() {
    this.userStopped = true;
    this.isListening = false;
    this._teardown();
    this._emitStatus('stopped');
  }

  // Called by Chat.jsx after send — just clear the utterance buffer.
  // WebSocket stays open for continuous listening.
  restart() { this._buf = ''; }

  // Called by Chat.jsx on Send: force Deepgram to finalize the current
  // utterance NOW so its tail comes back immediately, then discard that
  // flushed tail (it was already included in the sent message).
  flush() {
    this._buf = '';
    this._discardUntil = Date.now() + 800;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try { this.socket.send(JSON.stringify({ type: 'Finalize' })); } catch {}
    }
  }

  onTranscript(cb) { this.onTranscriptCallback = cb; }
  onStatus(cb)     { this.onStatusCallback     = cb; }

  // ── Microphone ──────────────────────────────────────────────────────────────

  async _getMic() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      return true;
    } catch (e) {
      console.error('[DG] Mic denied:', e.message);
      this._emitStatus('error');
      return false;
    }
  }

  // ── Deepgram WebSocket ───────────────────────────────────────────────────────

  _scheduleReconnect() {
    if (this.userStopped || this._gaveUp) return;
    clearTimeout(this.reconnTimer);
    console.log(`[DG] Reconnecting in ${this.reconnDelay}ms…`);
    this.reconnTimer = setTimeout(() => {
      if (!this.userStopped) this._connect();
    }, this.reconnDelay);
    this.reconnDelay = Math.min(this.reconnDelay * 2, 8000);
  }

  async _connect() {
    const seq = ++this._connectSeq;   // any earlier in-flight _connect() is now stale
    console.log('[DG] Connecting…');

    let auth;
    try {
      auth = await fetchDeepgramToken();
      this._tokenRetries = 0;
    } catch (err) {
      if (this.userStopped || seq !== this._connectSeq) return;
      if (err instanceof TokenAuthError) {
        console.error('[DG] Not authenticated — stopping.');
        this._giveUp();
        return;                       // no retry — the user must sign in again
      }
      this._tokenRetries += 1;
      console.error(`[DG] Could not get a speech token (${this._tokenRetries}/${MAX_TOKEN_RETRIES}):`, err.message);
      this._emitStatus('error');
      if (this._tokenRetries >= MAX_TOKEN_RETRIES) {
        console.error('[DG] Giving up on the speech token.');
        this._giveUp();
        return;
      }
      this._scheduleReconnect();
      return;
    }
    if (this.userStopped || seq !== this._connectSeq) return;

    // Drop any socket from a superseded attempt before opening a new one.
    if (this.socket) { try { this.socket.close(); } catch {} this.socket = null; }

    // Browser WebSocket auth via subprotocol array (no Authorization header needed).
    // scheme is "bearer" for grant tokens, "token" for a raw API key (dev only).
    const socket = new WebSocket(DG_URL, [auth.scheme, auth.token]);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    let opened = false;

    socket.onopen = () => {
      if (this.socket !== socket) { try { socket.close(); } catch {} return; }
      opened = true;
      this._connFailures = 0;
      console.log('[DG] Connected ✓');
      this.reconnDelay = 1000;
      this._startRecorder();
      this._startKeepAlive();
      this._emitStatus('listening');
    };

    socket.onmessage = (evt) => {
      if (this.socket !== socket) return;
      try { this._handle(JSON.parse(evt.data)); } catch {}
    };

    socket.onerror = () => {
      console.error('[DG] WebSocket error — token expired / network');
    };

    socket.onclose = (evt) => {
      if (this.socket !== socket) return;   // superseded — ignore its close
      console.log(`[DG] Closed (${evt.code})`);
      this._stopRecorder();
      this._stopKeepAlive();

      // A socket that never opened = a rejected handshake (bad token scheme,
      // revoked key, plan issue). Cap these so we don't loop forever minting
      // grants Deepgram won't accept.
      if (!opened) {
        this._connFailures += 1;
        if (this._connFailures >= MAX_CONNECT_FAILURES) {
          console.error('[DG] Deepgram keeps rejecting the connection — giving up.');
          this._giveUp();
          return;
        }
      }
      this._scheduleReconnect();
    };
  }

  _giveUp() {
    this._gaveUp = true;   // hard stop — no _scheduleReconnect() can re-arm
    this.isListening = false;
    this._teardown();
    this._emitStatus('error');
  }

  _handle(data) {
    // ── Transcript results ──
    if (data.type === 'Results') {
      const alt  = data.channel?.alternatives?.[0];
      const text = (alt?.transcript || '').trim();
      if (!text) return;

      // Drop the flushed tail of a just-sent utterance
      if (Date.now() < this._discardUntil) {
        if (data.is_final) this._buf = '';
        return;
      }

      if (data.is_final) {
        this._buf = this._buf ? this._buf + ' ' + text : text;

        if (data.speech_final) {
          // Full utterance done → send to Chat as final
          this._tx(this._buf, '');
          this._buf = '';
        } else {
          // More speech coming — show accumulated buf as interim
          this._tx('', this._buf);
        }
      } else {
        // Interim (unconfirmed) word — preview only
        const preview = this._buf ? this._buf + ' ' + text : text;
        this._tx('', preview);
      }
    }

    // ── UtteranceEnd: safety net when speech_final never fires ──
    if (data.type === 'UtteranceEnd' && this._buf) {
      if (Date.now() < this._discardUntil) { this._buf = ''; return; }
      this._tx(this._buf, '');
      this._buf = '';
    }

    // ── SpeechStarted ──
    if (data.type === 'SpeechStarted') {
      this._emitStatus('listening');
    }
  }

  _tx(finalText, interim) {
    if (this.onTranscriptCallback)
      this.onTranscriptCallback({ final: finalText, interim });
  }

  _emitStatus(status) {
    if (this.onStatusCallback) this.onStatusCallback(status);
  }

  // ── MediaRecorder ────────────────────────────────────────────────────────────

  _startRecorder() {
    if (!this.stream) return;
    this._stopRecorder();   // never leave a prior recorder running (race safety)

    const mime =
      MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
      MediaRecorder.isTypeSupported('audio/webm')             ? 'audio/webm' : '';

    try {
      this.recorder = mime
        ? new MediaRecorder(this.stream, { mimeType: mime })
        : new MediaRecorder(this.stream);
    } catch {
      this.recorder = new MediaRecorder(this.stream);
    }

    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0 && this.socket?.readyState === WebSocket.OPEN)
        this.socket.send(e.data);
    };

    this.recorder.onerror = (e) => console.error('[Recorder] Error:', e.error);

    // 250ms chunks = 4 sends/sec — low latency without overwhelming socket
    this.recorder.start(250);
    console.log('[Recorder] Started —', mime || 'default codec');
  }

  _stopRecorder() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try { this.recorder.stop(); } catch {}
    }
    this.recorder = null;
  }

  // ── Keep-alive (prevent Deepgram 10s idle timeout) ───────────────────────────

  _startKeepAlive() {
    this._stopKeepAlive();
    this.keepAliveInt = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN)
        this.socket.send(JSON.stringify({ type: 'KeepAlive' }));
    }, 8000);
  }

  _stopKeepAlive() {
    if (this.keepAliveInt) {
      clearInterval(this.keepAliveInt);
      this.keepAliveInt = null;
    }
  }

  // ── Full cleanup ─────────────────────────────────────────────────────────────

  _teardown() {
    if (this.reconnTimer) { clearTimeout(this.reconnTimer); this.reconnTimer = null; }
    this._stopKeepAlive();
    this._stopRecorder();

    if (this.socket) {
      try {
        if (this.socket.readyState === WebSocket.OPEN)
          this.socket.send(JSON.stringify({ type: 'CloseStream' }));
        this.socket.close();
      } catch {}
      this.socket = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
  }
}

const audioService = new AudioService();
export default audioService;
